<template>
  <div transition="fade" v-if="components.length">
    <div v-for="(modal, index) in components" :key="modal.uniqueKey"
      :class="{ 'overlay': !modal.props.right && !modal.props.left }"
      :style="getLayerStyle(index)">
      <transition
        :name="modal.props.right ? 'modal-animation-right' : modal.props.left ? 'modal-animation-left' : 'modal-animation'"
        appear>
        <div
          :class="modal.props.right ? 'modal-right' : modal.props.left ? 'modal-left' : 'modal'"
          :style="getLayerStyle(index)"
        >
          <div class="modal-content modal-dialog modal-lg" :class="{
            'modal-dialog-full': modal.props.fullScreen,
            'modal-dialog-middle': modal.props.middle,
            'modal-dialog-right': modal.props.right,
            'modal-dialog-left': modal.props.left
          }">
            <div>
              <component :is="modal.component" v-bind="modal.props" :key="modal.uniqueKey" />
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { newPopup, closePopup, messageBus, closeAllPopup } from "./MessageBus";

export default {
  name: "PopupContainer",
  data() {
    return {
      components: [],
      _pendingCloseEvents: [],
      _processingCloseTimer: null,
      primaryLevelComponents: [
        'Cto', 'ClientMap', 'Cable', 'PopMap', 'AmendmentMap', 'PoleMap', 'PolygonMap',
        'TechnicalReserve', 'VirtualCircuit', 'CordoalhaMap',
        'MaintenancePoint', 'VerticalCondominium', 'EquipmentLimit',
        'folderChangeMap', 'StreetView',
        'FolderList', 'LayersMap', 'DamagedFiberList',
        'MessageList', 'ClientWithoutConnection', 'deactivationClient',
        'ClientWithoutOltAssociation', 'Building'
      ],
    };
  },
  methods: {
    getComponentType(component) {
      if (typeof component === 'string') {
        return component;
      }
      if (typeof component === 'function') {
        return component.name || 'unknown';
      }
      if (component && typeof component === 'object') {
        return component.name || component.__name ||
          (component.options && component.options.name) || 'unknown';
      }
      return 'unknown';
    },
    getComponentLevel(componentType) {
      return this.primaryLevelComponents.includes(componentType) ? 'primary' : 'secondary';
    },
    getLayerStyle(index) {
      return {
        zIndex: 1005 + index,
      };
    },
    moveComponentToTop(index, payload) {
      if (index !== -1) {
        this.components.splice(index, 1);
      }
      this.components.push(payload);
    },
    
    addComponent(payload) {
      payload.uniqueKey = `${Date.now()}_${Math.random()}`;
      const componentType = this.getComponentType(payload.component);
      const shouldKeepOpen = Boolean(payload.props && payload.props.keepOpen);

      const componentLevel = this.getComponentLevel(componentType);
      const existingSameTypeIndex = this.components.findIndex((element) => {
        return this.getComponentType(element.component) === componentType;
      });

      if (shouldKeepOpen && existingSameTypeIndex !== -1) {
        this.moveComponentToTop(existingSameTypeIndex, payload);
        return;
      }

      if (componentLevel === 'primary') {
        const existingPrimaryIndex = this.components.findIndex((element) => {
          const existingType = this.getComponentType(element.component);
          return this.getComponentLevel(existingType) === 'primary' && !(element.props && element.props.keepOpen);
        });

        if (existingPrimaryIndex !== -1) {
          this.moveComponentToTop(existingPrimaryIndex, payload);

          this.components = this.components.filter((element) => {
            const existingType = this.getComponentType(element.component);
            const isPrimary = this.getComponentLevel(existingType) === 'primary';
            const keepOpen = Boolean(element.props && element.props.keepOpen);
            const isCurrentPayload = element.uniqueKey === payload.uniqueKey;
            return isPrimary && (keepOpen || isCurrentPayload);
          });
        } else {
          this.components.push(payload);
        }
      } else {
        if (existingSameTypeIndex !== -1) {
          this.moveComponentToTop(existingSameTypeIndex, payload);
        } else {
          this.components.push(payload);
        }
      }
    },
    
    closeComponent(componentId) {
      if (!this._pendingCloseEvents) this._pendingCloseEvents = [];
      this._pendingCloseEvents.push(componentId);
      if (!this._processingCloseTimer) {
        this._processingCloseTimer = setTimeout(() => {
          this._processingCloseTimer = null;
          const events = this._pendingCloseEvents.splice(0);
          const targeted = events.filter((events) => events !== undefined && events !== null);
          targeted.forEach((id) => {
            const index = this.components.findIndex((modal) => modal.props && modal.props.componentId === id);
            if (index !== -1) this.components.splice(index, 1);
          });
          const genericCount = events.length - targeted.length;
          for (let i = 0; i < genericCount; i++) {
            if (this.components.length) {
              this.components.pop();
            }
          }
        }, 0);
      }
    },
  },
  mounted() {
    messageBus.$off(newPopup);
    messageBus.$on(newPopup, (payload) => {
      this.addComponent(payload);
    });
    messageBus.$on(closePopup, (componentId) => {
      // previous code called requestCloseEvent which isn't defined here
      // use the internal closeComponent method to handle pending closes
      this.closeComponent(componentId);
    });
    messageBus.$on(closeAllPopup, () => {
      this.components.splice(0, this.components.length);
    });
  },
};
</script>

<style lang="sass">
@import "../../public/static/sass/_variables.scss"

.overlay
  position: fixed
  width: 100%
  height: 100%
  top: 0
  left: 0
  right: 0
  bottom: 0
  background-color: $color-black-50
  z-index: 1005

.modal-animation-enter-active, .modal-animation-right-enter-active, .modal-animation-left-enter-active
  /* Animate transform and opacity to achieve a visible slide effect */
  transition: transform 0.18s cubic-bezier(.2,.9,.2,1), opacity 0.18s cubic-bezier(.2,.9,.2,1)

/* Also animate the inner dialog for side panels and center dialog. This ensures
   the fixed-position inner dialog slides instead of only the wrapper. */
.modal-animation-right-enter-active .modal-dialog-right,
.modal-animation-left-enter-active .modal-dialog-left,
.modal-animation-enter-active .modal-dialog
  transition: transform 0.18s cubic-bezier(.2,.9,.2,1), opacity 0.18s cubic-bezier(.2,.9,.2,1)

.modal-animation-leave-active, .modal-animation-right-leave-active, .modal-animation-left-leave-active
  /* Disable leave/closing animation so the popup is removed immediately
     — avoids the strange shrinking/flicker effect on close. */
  transition: none

.modal-animation-enter
  /* Start slightly below and transparent so the element slides up into place */
  opacity: 0
  transform: translateY(20px)

.modal-animation-enter .modal-dialog
  opacity: 0
  transform: translateY(20px)

.modal-animation-enter-to .modal-dialog,
.modal-animation-enter-active .modal-dialog
  transform: translateY(0)
  opacity: 1
  
.modal-animation-right-enter
  /* keep wrapper neutral for side panels; the inner dialog will handle the slide */

.modal-animation-right-enter .modal-dialog-right
  /* start off-screen to the right; keep opacity at 1 so it feels like sliding, not fading */
  opacity: 1
  transform: translateX(100%)

.modal-animation-right-enter-to .modal-dialog-right,
.modal-animation-right-enter-active .modal-dialog-right
  transform: translateX(0)
  opacity: 1
  
.modal-animation-left-enter
  /* keep wrapper neutral for side panels; the inner dialog will handle the slide */

.modal-animation-left-enter .modal-dialog-left
  /* start off-screen to the left; keep opacity at 1 so it feels like sliding, not fading */
  opacity: 1
  transform: translateX(-100%)

.modal-animation-left-enter-to .modal-dialog-left,
.modal-animation-left-enter-active .modal-dialog-left
  transform: translateX(0)
  opacity: 1
  
@media only screen and (max-width: 576px)
  .modal-animation-right-enter,
  .modal-animation-left-enter
    transform: translateY(100%)
    opacity: 0

  /* On small screens, make center modals slide up from bottom (mobile-like) */
  .modal-animation-enter
    transform: translateY(100%)
    opacity: 0

/* Ensure leave state doesn't apply any transform/opacity change so hide is immediate */
.modal-animation-leave,
.modal-animation-right-leave,
.modal-animation-left-leave
  opacity: 1
  transform: none

.blur
  filter: blur(1px)
  opacity: 0.4

.modal
  display: block !important
  /* z-index: 1007 Dialog broken  */

.modal-right
  display: block !important
  position: fixed
  top: 0
  right: 0
  height: 100%
  margin: 0

.modal-left
  display: block !important
  position: fixed
  top: 0
  left: 0
  height: 100%
  margin: 0


.modal-dialog-full
  width: 100% !important
  height: 100vh !important
  padding: 0 !important
  margin: 0 !important
  min-width: 100% !important
  min-height: 100vh !important
  max-height: 100vh !important
  position: fixed !important
  top: 0 !important
  left: 0 !important
  right: 0 !important
  bottom: 0 !important
  overflow: hidden !important

.modal-dialog-right 
  position: fixed !important
  top: 61px !important
  right: 0 !important
  height: 100% !important
  width: 40vw !important
  max-width: none !important
  min-width: 400px !important
  margin: 0 !important
  padding: 0 !important

.modal-dialog-left
  position: fixed !important
  top: 61px !important
  left: 0 !important
  height: 100% !important
  width: 40vw !important
  max-width: none !important
  min-width: 400px !important
  margin: 0 !important
  padding: 0 !important
  
.modal-content-full
  height: 100vh !important
  border-radius: 0 !important

.modal-dialog-middle
  max-width: 70% !important

@media only screen and (max-width: 1199.98px)
  .modal-dialog
    max-width: 800px !important
  .modal-dialog-middle
    max-width: 90% !important

  .modal-dialog-right,
  .modal-dialog-left
    width: 50vw !important
    min-width: 300px !important

/* Smartphones */
@media only screen and (max-width: 576px)
  .modal-dialog-right,
  .modal-dialog-left,
  .modal-dialog-full
    width: 100vw !important
    min-width: 100% !important
    position: unset !important
    min-height: unset !important
  .modal-dialog-full .modal-content-full
    width: 100%
    height: 100% !important
    min-height: 100% !important
    margin: 0 !important
  .modal-dialog-middle
    max-width: 100% !important
    margin: 10px !important
  .modal-right, 
  .modal-left
    position: relative
    top: unset

</style>
